import {
    Button, ClickEvent, DataHeader, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, Label,
    LookupModelSearchEvent, SlideoutDecorator, Tab, Table, TableRow,
    TableRowDisplayEvent,
    Textbox,
    ValidationResult
} from "@mcleod/components";
import { CarrierTargetLaneRate } from "./CarrierTargetLaneRate";
import { AutogenLayoutCarrierTargetRate } from "./autogen/AutogenLayoutCarrierTargetRate";
import { LocationByTypeProps, LocationByTypeUtil } from "../LocationByTypeUtil";
import { RateUtil } from "../RateUtil";
import { ModelRow } from "@mcleod/core";
import { LinkedModel } from "@mcleod/core/src/ModelRow";
import { CustomerFscQuickInfo } from "@mcleod/ar";


const LOCATION_BY_TYPE_PROPS: LocationByTypeProps = {
    cityType: "C",
    stateType: "S",
    zoneType: "O",
    locationType: "L",
    useDefault: true
}

export class CarrierTargetRate extends AutogenLayoutCarrierTargetRate {

    onLoad(): void | Promise<void> {
        this.layoutTableComment.displayCopyOrderFields = false;
        this.styleTab(this.tabComments);
        this.layoutTableComment.tableComments.addContentsChangedListener(event => this.tableCommentOnContentsChanged(event));
        this.layoutTableComment.tableComments.addRowCreateListener(event => this.tableCommentOnRowCreate(event));

        this.tableCarrierLaneRates.onEditLayoutLoaded = (decorator, tableRow) => {
            decorator.multiButton.dropdownItems = null;
            const isSearch = this.mainDataSource.mode === DataSourceMode.SEARCH;
            decorator.multiButton.caption = `Apply ${isSearch ? 'Filters' : 'Edits'}`;
            decorator.labelTitle.text = `${isSearch ? 'Search' : 'Edit'} Target Lane Rate`;
            const carrierTargetLaneRate = decorator.layout as CarrierTargetLaneRate;
            carrierTargetLaneRate.activeRow.addAfterFieldUpdateListener((event) => carrierTargetLaneRate.syncPtToPt());
            carrierTargetLaneRate.syncPtToPt();
            decorator.doAfterSlideOut = (decoratorAfterEdit) => {
                this.syncLocationByType(tableRow);
                RateUtil.setCountyValuesIfNeeded(tableRow.data, "N", "origin_type", "origin_city_name", "origin_state", "origin_value");
                RateUtil.setCountyValuesIfNeeded(tableRow.data, "N", "destination_type", "destination_city", "destination_state", "destination_value");
                tableRow.displayComponentValues();
            }
            decorator.onValidate = (panelRow: ModelRow): ValidationResult[] => {
                if (!RateUtil.areValidDates(panelRow.getDate('effective_date'), panelRow.getDate('expiration_date'))) {
                    return [{ isValid: false, validationMessage: 'Effective date must be before expiration date', component: carrierTargetLaneRate.textboxEffectiveDate }];
                }
                return [{ isValid: !RateUtil.isDuplicateRateLane(tableRow.table.allRows, panelRow, true), component: carrierTargetLaneRate.textboxOrigin }]
            }
        };
        this.textboxFuelSurchargePay.quickInfoOverrideSearchMethod = CustomerFscQuickInfo.fuelSurchargePaySearchMethod;
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.promptToSaveChanges();
        dataHeader.showDelete = true;
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.color = "subtle.light";
            tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: false })
            if (tab["countLabel"] == null) {
                tab["countLabel"] = new Label({
                    fontBold: true, text: "0", fillRow: true,
                    color: "primary", rowBreak: false, marginLeft: 5
                });
                tab.heading.insert(tab["countLabel"], 1);
            }
        }
    }

    textboxOrderTypeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = 'Y';
    }

    textboxFuelSurchargePayBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.surcharge_type = 'R';
    }

    sourceCarrierRateAfterModeChange(event: DataSourceModeChangeEvent) {
        const isSearch = event.dataSource.mode === DataSourceMode.SEARCH;
        this.buttonAdd.visible = !isSearch;
        this.buttonSearch.visible = isSearch;
        this.textboxRateNumber.visible = isSearch;
        
        if (event.dataSource.mode == DataSourceMode.ADD) {
            event.dataSource.doBeforeDataSourcePost = async () => {
                this.activeRow.set('target_rate', 'Y');
                return true;
            }
        }

        if (event.oldMode === DataSourceMode.ADD && event.newMode === DataSourceMode.UPDATE) {
            const childLinks: LinkedModel[] = this.sourceCarrierRate.activeRow?.data["_child_links"];
            childLinks?.forEach(linkedModel => linkedModel.rows?.forEach(row => row._appending = false));
        }
    }

    tableCommentOnContentsChanged(event) {
        this.updateTabCountLabel(this.tabComments, event.getTable());
    }

    tableCommentOnRowCreate(event) {
        this.updateTabCountLabel(this.tabComments, event.getTable());
    }

    sourceCommentsAfterExecution(event: DataSourceExecutionEvent) {
        this.updateTabCountLabel(this.tabComments, this.layoutTableComment.tableComments);
    }

    updateTabCountLabel(tab: Tab, table: Table) {
        if (tab != null && tab["countLabel"] != null) {
            tab["countLabel"].text = table.dataSource.rowCount + "";
        }
    }

    tableRowOnDblClick(event: ClickEvent) {
        const tableRow = event.target as TableRow;
        tableRow.showEditLayout();
    }

    buttonSearchOnClick(event: ClickEvent) {
        this.tableCarrierLaneRates.allRows[0].showEditLayout();
    }

    buttonAddOnClick(event: ClickEvent) {
        const saveButton = this.createSaveButton();
        SlideoutDecorator.decorateAndSlideIn('lme/dispatch/rates/CarrierTargetLaneRate', {
            fillVerticalSpace: true,
            width: '75%',
            title: 'Add Target Lane Rate',
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            addlComponents: saveButton,
            layoutLoadListeners:async (event) => {
                const layout = event.target as CarrierTargetLaneRate;
                layout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await layout.mainDataSource.createBlankRow()]);
                layout.activeRow.addAfterFieldUpdateListener((event) => layout.syncPtToPt());
                layout.syncPtToPt();
            },
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                saveButton.addClickListener(event => {
                    saveButton.busy = true;
                    const carrierTargetRate = decorator.layout as CarrierTargetLaneRate;
                    decorator.layout.mainDataSource.addValidationListener((event) => {
                        const carrierLaneRateRow = decorator.layout.activeRow;
                        if (!RateUtil.areValidDates(carrierLaneRateRow.getDate('effective_date'), carrierLaneRateRow.getDate('expiration_date'), false)) {
                            const message = 'Effective date must be before expiration date';
                            event.validationResults.push({
                                isValid: false,
                                validationMessage: message,
                                caption: message,
                                component: carrierTargetRate.textboxEffectiveDate
                            });
                        }
                        else {
                            const isDuplicate = RateUtil.isDuplicateRateLane(this.tableCarrierLaneRates.allRows, carrierLaneRateRow, false);
                            if (isDuplicate) {
                                const message = "A target rate is already defined for this lane/commodity/order type/trailer type/revenue with overlapping effective and expiration dates.";
                                event.validationResults.push({
                                    isValid: false,
                                    validationMessage: message,
                                    caption: message,
                                    component: carrierTargetRate.textboxOrigin
                                });
                            }
                        }
                    })
                    if (decorator.layout.mainDataSource.validate()){
                        const targetLaneRate = decorator.layout.activeRow;
                        decorator.slideOut();
                        RateUtil.setCountyValuesIfNeeded(targetLaneRate, "N", "origin_type", "origin_city_name", "origin_state", "origin_value");
                        RateUtil.setCountyValuesIfNeeded(targetLaneRate, "N", "destination_type", "destination_city", "destination_state", "destination_value");
                        
                        this.setFuelSurchargeLookupModelDescription(targetLaneRate);
                           
                        this.tableCarrierLaneRates.addRow(targetLaneRate, null, {display: true, addToData: true});
                    }
                    saveButton.busy = false;
                });
            }
        });
    }

    private setFuelSurchargeLookupModelDescription(row: ModelRow) {
        const fscLookupData = row.getFirstLookupModelData("fuel_surcharge_pay");
        if (fscLookupData != null) {
            row.set("customer_fuel_descr", fscLookupData.get("descr"));
        }
    }

    createSaveButton() {
        return new Button({
            id: "carrierLaneRateSaveButton",
            caption: "Add",
            backgroundColor: "primary",
            minWidth: 128,
            borderWidth: 0,
            rowBreak: false
        });
    }

    tableCarrierLaneRatesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        this.syncLocationByType(tableRow);
    }

    syncLocationByType(tableRow: TableRow) {
        const modelRow = tableRow.data;
        const textboxOrigin = tableRow.findComponentById('textboxOrigin') as Textbox;
        const textboxDestination = tableRow.findComponentById('textboxDestination') as Textbox;

        if (!modelRow.isNull('origin_type') && textboxOrigin != null) {
            const originType = modelRow.getString('origin_type');
            LocationByTypeUtil.setLookupByType(originType, textboxOrigin, false, LOCATION_BY_TYPE_PROPS);
        }
        if (!modelRow.isNull('destination_type') && textboxDestination != null) {
            const destinationType = modelRow.getString('destination_type', null);
            LocationByTypeUtil.setLookupByType(destinationType, textboxDestination, false, LOCATION_BY_TYPE_PROPS);
        }
    }
}
